import {
  IconButton,
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Grid,
  Divider,
  useMediaQuery,
  TableHead,
  TableRow,
  Table,
  TableContainer,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  SvgIcon,
  Link,
} from '@material-ui/core';
import { ReactComponent as RefundIcon } from '../refund.svg';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import React from 'react';
import { Payment, PaymentStatus, RefundStatus, PaymentRequest } from '../gql-types.generated';
import { paymenteStatusDisplay } from '../util/PaymentStatus';
import CloseIcon from '@material-ui/icons/Close';
import { v4 as uuid4 } from 'uuid';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
    },
    label: {
      color: theme.palette.grey[700],
      paddingLeft: '16px',
    },
    value: {
      color: theme.palette.common.black,
    },
    cell: {
      color: theme.palette.grey[800],
    },
    headerText: {
      height: '100%',
      width: '100%',
    },
    refundButton: {
      marginLeft: theme.spacing(2),
    },
    headerHeadText: {
      letterSpacing: 1.5,
      fontSize: 12,
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: 2,
      color: theme.palette.grey[500],
    },
    contentHeaderText: {
      padding: theme.spacing(2, 2, 0, 2),
    },
    contentDivider: {
      width: 1,
    },
    communicationTable: {
      width: '100%',
    },
    communicationTableGridWrapper: {
      flexGrow: 1,
    },
    tooltip: {
      cursor: 'pointer',
      marginLeft: '0.3rem',
    },
    copyContent: {
      textAlign: 'right',
    },
    titleText: {
      fontSize: '16px',
      fontWeight: 'bold',
    },
  }),
);
interface PaymentDetailsProps {
  open: boolean;
  onClose: () => void;
  paymentRecord?: Payment;
  refund: (paymentRequest: Payment) => void;
  defaultCurrency?: string | null;
  openPaymentRequestDetails: (paymentRequest: PaymentRequest | undefined) => void;
}
const PaymentDetails: React.FC<PaymentDetailsProps> = props => {
  const classes = useStyles();
  const { paymentRecord, onClose, refund, defaultCurrency, open, openPaymentRequestDetails } = props;
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  let refunded = 0;
  paymentRecord?.refunds.map(refund => {
    if (refund?.status === RefundStatus.Completed || refund?.status === RefundStatus.Pending) {
      refunded += refund.amount || 0;
    }
  });
  //A payment is refundable if it is completed and has not been fully refunded/disputed
  const refundable = paymentRecord?.status === PaymentStatus.Completed && !!paymentRecord?.amount && refunded < paymentRecord?.amount;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableBackdropClick={false}
      disableEscapeKeyDown={false}
      maxWidth={paymentRecord?.refunds && paymentRecord?.refunds.length > 0 ? 'md' : 'sm'}
    >
      <>
        <DialogTitle data-cy="payment-details-modal">
          <Grid container spacing={1} className={classes.header}>
            <Grid item xs={11} className={classes.headerText}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" className={classes.headerHeadText}>
                    PAYMENT DETAILS
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container>
                    <Grid item xs={10}>
                      <Typography className={classes.titleText}>Payment ID {paymentRecord?.id}</Typography>
                    </Grid>
                    <Grid item xs={2} className={classes.copyContent}>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          navigator.clipboard.writeText(paymentRecord?.id || '');
                        }}
                        data-cy="payment-id-copy"
                      >
                        COPY
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                title="close"
                className={classes.closeButton}
                onClick={onClose}
                data-cy="payment-details-close"
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />
        {refundable && (
          <>
            <Grid container>
              <Grid item xs={12}>
                <Button
                  className={classes.refundButton}
                  size="small"
                  variant={'text'}
                  aria-label={'refund'}
                  color={'primary'}
                  startIcon={<SvgIcon component={RefundIcon} />}
                  onClick={() => {
                    if (paymentRecord) refund(paymentRecord);
                  }}
                  data-cy="payment-details-refund"
                >
                  Refund
                </Button>
              </Grid>
            </Grid>
            <Divider />
          </>
        )}
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12} md={paymentRecord?.refunds && paymentRecord?.refunds.length > 0 ? 6 : 12}>
              <Grid container direction="row" spacing={1} alignItems="stretch">
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Status
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {
                      paymenteStatusDisplay.find(item => {
                        return item.name === paymentRecord?.status;
                      })?.display
                    }
                  </Typography>
                </Grid>
                {paymentRecord?.status === PaymentStatus.Canceled && paymentRecord?.cancelReason && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body1" className={classes.label}>
                        Cancelled Reason
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1" className={classes.value}>
                        {paymentRecord?.cancelReason}
                      </Typography>
                    </Grid>
                  </>
                )}
                {paymentRecord?.status === PaymentStatus.Failed && paymentRecord?.failureReason && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body1" className={classes.label}>
                        Failure Reason
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1" className={classes.value}>
                        {paymentRecord?.failureReason}
                      </Typography>
                    </Grid>
                  </>
                )}
                {paymentRecord?.status === PaymentStatus.Pending && paymentRecord?.pendingReason && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body1" className={classes.label}>
                        Pending Reason
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1" className={classes.value}>
                        {paymentRecord?.pendingReason}
                      </Typography>
                    </Grid>
                  </>
                )}
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    {!!paymentRecord?.amountDisputed && paymentRecord.amountDisputed > 0 ? 'Disputed ' : ''}Amount
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {typeof paymentRecord?.amount === 'number'
                      ? new Intl.NumberFormat('en', {
                          style: 'currency',
                          currency: defaultCurrency || 'USD',
                          currencyDisplay: 'symbol',
                        }).format(paymentRecord.amount / 100)
                      : '- -'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Paid By
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {paymentRecord?.initiatedBy || '- -'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Grid container>
                    <Typography variant="body1" className={classes.label}>
                      Capture Type
                    </Typography>
                    <Tooltip
                      title="When a customer makes a payment, the payment information needs to be captured and sent to their bank for processing. Payments can be captured immediately or manually."
                      className={classes.tooltip}
                    >
                      <HelpOutlineOutlinedIcon htmlColor="grey" fontSize="small" />
                    </Tooltip>
                  </Grid>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {paymentRecord?.immediateCapture ? 'Immediate' : 'Manual'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Created On
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {paymentRecord?.createdAt ? new Date(paymentRecord.createdAt as string).toLocaleDateString() : '- -'}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body1" className={classes.label}>
                    Paid On
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body1" className={classes.value}>
                    {paymentRecord?.completedTimestamp
                      ? new Date(paymentRecord.completedTimestamp as string).toLocaleDateString()
                      : '- -'}
                  </Typography>
                </Grid>
                {paymentRecord?.paymentRequest && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body1" className={classes.label}>
                        Payment Request
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Link
                        href="#"
                        onClick={() => {
                          if (paymentRecord.paymentRequest !== null) openPaymentRequestDetails(paymentRecord.paymentRequest);
                        }}
                      >
                        <Typography variant="body1">{paymentRecord?.paymentRequest?.referenceNumber}</Typography>
                      </Link>
                    </Grid>
                  </>
                )}
                {!!paymentRecord?.feeAmount && paymentRecord?.feeAmount > 0 && (
                  <>
                    <Grid item xs={4}>
                      <Typography variant="body1" className={classes.label}>
                        Fee Amount
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="body1" color="error">
                        {typeof paymentRecord?.feeAmount === 'number'
                          ? new Intl.NumberFormat('en', {
                              style: 'currency',
                              currency: defaultCurrency || 'USD',
                              currencyDisplay: 'symbol',
                            }).format(paymentRecord.feeAmount / 100)
                          : '- -'}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            {paymentRecord?.refunds && paymentRecord?.refunds.length > 0 && (
              <>
                <Grid item xs={matches ? 12 : undefined} className={matches ? '' : classes.contentDivider}>
                  <Divider orientation={matches ? 'horizontal' : 'vertical'} />
                </Grid>
                <Grid item xs={12} md={'auto'} className={classes.communicationTableGridWrapper}>
                  <Typography variant="subtitle2" className={classes.contentHeaderText}>
                    Refund History
                  </Typography>
                  <TableContainer className={classes.communicationTable}>
                    <Table aria-label="refunds">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.cell}>Type</TableCell>
                          <TableCell className={classes.cell}>Amount</TableCell>
                          <TableCell className={classes.cell}>Reason</TableCell>
                          <TableCell className={classes.cell}>Refunded On</TableCell>
                        </TableRow>
                      </TableHead>
                      {paymentRecord && (
                        <TableBody data-cy="payment-details-refund-table">
                          {paymentRecord.refunds.map(refund => {
                            if (refund) {
                              return (
                                <TableRow key={refund.id || uuid4()}>
                                  <TableCell className={classes.cell}>
                                    {refund.amount === refund.payment.amount ? 'Full' : 'Partial'}
                                  </TableCell>
                                  <TableCell className={classes.cell}>
                                    {typeof refund.amount === 'number'
                                      ? new Intl.NumberFormat('en', {
                                          style: 'currency',
                                          currency: defaultCurrency || 'USD',
                                          currencyDisplay: 'symbol',
                                        }).format(refund.amount / 100)
                                      : '- -'}
                                  </TableCell>
                                  <TableCell className={classes.cell}>{refund.refundReason}</TableCell>
                                  <TableCell className={classes.cell}>
                                    {refund.createTime ? new Date(refund.createTime as string).toLocaleDateString() : ''}
                                  </TableCell>
                                </TableRow>
                              );
                            }
                            return <></>;
                          })}
                        </TableBody>
                      )}
                    </Table>
                  </TableContainer>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
      </>
    </Dialog>
  );
};
export default PaymentDetails;
